import { useCallback, useMemo } from 'react';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { CriteoClient, CriteoRecommendationsResponse, type CriteoRequestContext } from '@jbhi-fi/apis/src/criteo';
import { CriteoPlacementGroup, CriteoSponsoredProduct } from '@jbhi-fi/apis/src/criteo/types';
import { useFeatureFlag } from '../useFeatureFlag/useFeatureFlag';
import { usePublicApiClient } from '@jbhi-fi/components/src/hooks/usePublicApiClient';
import { sha256 } from '@jbhi-fi/utils/src/sha256/sha256';

type GetSearchSponsoredProductsParams = {
  keywords: string;
  pageId?: string;
};

type GetCollectionSponsoredProductsParams = {
  collectionKey: string;
  pageId?: string;
  filters?: string;
};

type SponsoredProducts = Array<CriteoSponsoredProduct>;

type CriteoSponsoredProductsRequest = (
  criteoClient: CriteoClient,
  requestContext: CriteoRequestContext
) => ReturnType<CriteoClient['getSearchSponsoredProducts']>;

type UseCriteoSponsoredProductsReturn = {
  getSearchSponsoredProducts: (params: GetSearchSponsoredProductsParams) => Promise<SponsoredProducts>;
  getCollectionSponsoredProducts: (params: GetCollectionSponsoredProductsParams) => Promise<SponsoredProducts>;
  isSponsoredContentEnabled: boolean;
};

const SEARCH_PAGE_IDS = {
  desktop: 'viewSearchResult_API_desktop',
  mobile: 'viewSearchResult_API_mobile',
} as const;

const COLLECTION_PAGE_IDS = {
  desktop: 'viewCategory_API_desktop',
  mobile: 'viewCategory_API_mobile',
} as const;

const getEmail = () => {
  const email = window?.Jbhifi?.customerEmail
    ?.trim()
    .replace(/[,'";]/g, '')
    .toLowerCase();

  return email ? sha256(email) : undefined;
};

const getCriteoRequestContext = async (): Promise<CriteoRequestContext> => ({
  retailerVisitorId: window?.ga?.getAll()?.[0]?.get('clientId') ?? undefined,
  customerId: window?.Jbhifi?.customerId ? `${window.Jbhifi.customerId}` : undefined,
  email: await getEmail(),
});

const getSponsoredProductIds = (response?: CriteoRecommendationsResponse | unknown): SponsoredProducts => {
  if (!response) {
    return [];
  }

  const placements = (response as CriteoRecommendationsResponse)?.placements ?? [];

  return placements.flatMap((placementGroup: CriteoPlacementGroup) =>
    Object.values(placementGroup).flatMap(placementEntries =>
      placementEntries.flatMap(placement => placement.products ?? [])
    )
  );
};

const fetchSponsoredProductIds = async ({
  criteoClient,
  request,
}: {
  criteoClient: CriteoClient | null;
  request: CriteoSponsoredProductsRequest;
}): Promise<SponsoredProducts> => {
  if (!criteoClient) {
    return [];
  }

  const requestContext = await getCriteoRequestContext();
  const { data, error } = await request(criteoClient, requestContext);

  if (error || !data) {
    return [];
  }

  return getSponsoredProductIds(data);
};

export const useCriteoSponsoredProducts = (): UseCriteoSponsoredProductsReturn => {
  const { criteoClient } = usePublicApiClient();
  const isSponsoredContentEnabled = useFeatureFlag('isSponsoredContentEnabled') ?? false;
  const isDesktop = useBreakpoint('lg', 'up');
  const pageIdVariant = isDesktop ? 'desktop' : 'mobile';
  const publicCriteoClient = useMemo(
    () => (criteoClient instanceof CriteoClient ? criteoClient : null),
    [criteoClient]
  );

  const getSearchSponsoredProducts = useCallback(
    async ({
      keywords,
      pageId = SEARCH_PAGE_IDS[pageIdVariant],
    }: GetSearchSponsoredProductsParams): Promise<SponsoredProducts> => {
      const trimmedKeywords = keywords.trim();

      if (!trimmedKeywords || !isSponsoredContentEnabled) {
        return [];
      }

      return await fetchSponsoredProductIds({
        criteoClient: publicCriteoClient,
        request: (client, requestContext) =>
          client.getSearchSponsoredProducts({
            keywords: trimmedKeywords,
            pageId,
            ...requestContext,
          }),
      });
    },
    [isSponsoredContentEnabled, pageIdVariant, publicCriteoClient]
  );

  const getCollectionSponsoredProducts = useCallback(
    async ({
      collectionKey,
      pageId = COLLECTION_PAGE_IDS[pageIdVariant],
      filters,
    }: GetCollectionSponsoredProductsParams): Promise<SponsoredProducts> => {
      const trimmedCollectionKey = collectionKey.trim();

      if (!trimmedCollectionKey || !isSponsoredContentEnabled) {
        return [];
      }

      return await fetchSponsoredProductIds({
        criteoClient: publicCriteoClient,
        request: (client, requestContext) =>
          client.getCollectionSponsoredProducts({
            collectionKey: trimmedCollectionKey,
            pageId,
            filters,
            ...requestContext,
          }),
      });
    },
    [publicCriteoClient, isSponsoredContentEnabled, pageIdVariant]
  );

  return {
    getSearchSponsoredProducts,
    getCollectionSponsoredProducts,
    isSponsoredContentEnabled,
  };
};
